/**
 * THE CABINET'S ONE DOOR TO THE GENERATED TYPES (auth, support and application keep their own).
 *
 * `shared/types/generated/authors-api.d.ts` is produced from `docs/design/07-api/authors-api.yaml`
 * by `pnpm gen:types` (shared/types/index.ts, adr/0013: "the spec is authoritative"). Nothing in
 * this app hand-writes a request or a response shape. If a shape cannot be reached from here, the
 * operation does not exist — that is a finding to report, not a type to invent.
 *
 * WHY A RELATIVE IMPORT AND NOT A PACKAGE NAME. `@moonx/types` exists as a workspace package but
 * its `index.ts` exports nothing (`export {}` — "Empty at M0 by design") and it is not a dependency
 * of `@moonx/authors-web`, so `node_modules/@moonx/types` is not linked into this app. adr/0013
 * rules "no path aliases — plain relative imports", and 12-build-plan.md fails M0 on a
 * tsconfig `paths` entry, so a relative path is the sanctioned way across. It is a TYPE-ONLY
 * import: nothing of it survives into the bundle, so Vite never resolves it at all.
 *
 * The re-exports below are narrowing, not restating. Every alias resolves through
 * `components['schemas'][…]` or `operations[…]`, so a spec change lands here as a compile error
 * rather than as a screen that quietly renders the wrong field.
 */
import type { components, operations } from '../../../../../../shared/types/generated/authors-api';

/* ---------------------------------------------------------------------------------------------
 * Operation plumbing.
 *
 * These four helpers are how a method signature is derived from the spec rather than copied out of
 * it. They read the SAME generated interface the server will be validated against, so a query
 * parameter that is added, renamed or removed upstream changes the call site here.
 * ------------------------------------------------------------------------------------------- */

/** Every operationId on authors-api. 68 of them at HEAD (wave 26). */
export type OperationName = keyof operations;

type JsonBody<R> = R extends { readonly content: { readonly 'application/json': infer T } }
  ? T
  : never;

/** The `application/json` body of an operation's success response (200 or 201). */
export type OperationResult<K extends OperationName> = operations[K]['responses'] extends {
  readonly 200: infer R;
}
  ? JsonBody<R>
  : operations[K]['responses'] extends { readonly 201: infer R }
    ? JsonBody<R>
    : void;

/** An operation's query string, as the spec declares it. */
export type OperationQuery<K extends OperationName> = operations[K] extends {
  readonly parameters: { readonly query?: infer Q };
}
  ? NonNullable<Q>
  : never;

/** An operation's `application/json` request body. */
export type OperationBody<K extends OperationName> = operations[K] extends {
  readonly requestBody: { readonly content: { readonly 'application/json': infer B } };
}
  ? B
  : never;

/* ---------------------------------------------------------------------------------------------
 * The schemas these screens render.
 * ------------------------------------------------------------------------------------------- */

export type Problem = components['schemas']['Problem'];

/**
 * The 65 stable machine codes of shared.yaml's closed `ErrorCode` (src/common/errors/error-code.ts).
 * `Problem.error_code` is REQUIRED and is exactly this closed enum, which is why no code in this
 * app is ever spelled as a bare string: an invented one does not compile.
 *
 * ONE CONSEQUENCE WORTH NAMING HERE because it bites the fixture: there is no not-found member.
 * `UPLOAD_OBJECT_NOT_FOUND` is upload-specific, and 28 of the 65 operations declare a 404 whose
 * body is this same `Problem`. See ./problem.ts `NotFoundFailure`.
 */
export type ErrorCode = components['schemas']['ErrorCode'];
export type PublicId = components['schemas']['PublicId'];
export type ServerTime = components['schemas']['ServerTime'];
export type MinorUnits = components['schemas']['MinorUnits'];
export type CurrencyCode = components['schemas']['CurrencyCode'];
export type Cursor = components['parameters']['Cursor'];

export type AuthorSession = components['schemas']['AuthorSession'];

export type Dashboard = components['schemas']['Dashboard'];
export type DashboardTiles = Dashboard['tiles'];

/**
 * One number with its state. `ok`, `no_data` (the range really is empty and zero is true) and
 * `not_reported_yet` (the event is in the vocabulary and no shipped client emits it) —
 * authors-api.d.ts MetricTile. A BARE ZERO IS NEVER CORRECT for any of these.
 */
export type MetricTile = components['schemas']['MetricTile'];
export type MetricState = MetricTile['state'];

export type ArticleStats = components['schemas']['ArticleStats'];
export type ArticleStatsPage = components['schemas']['ArticleStatsPage'];
/** A video's own figures (v1.0.29): videos are out of the statistics above. */
export type VideoStats = components['schemas']['VideoStats'];
/** Every day of a range with its figures - the Statistics chart's read (design 18). */
export type DashboardDays = components['schemas']['DashboardDays'];
export type DashboardDay = DashboardDays['days'][number];

export type ArticleStatus = components['schemas']['ArticleStatus'];
export type ArticleListItem = components['schemas']['ArticleListItem'];
export type ArticleListPage = components['schemas']['ArticleListPage'];

/**
 * What every author-side lifecycle move answers with. NOTE THAT IT IS NOT A LIST ROW: it carries
 * the move (`fromStatus`, `status`, `seq`, `occurredAt`) and not the article's title or cover. A
 * screen that wants the row back after a move re-reads the list; there is nothing to patch in
 * place from this, and pretending otherwise is how a list goes stale while looking fresh.
 */
export type TransitionResult = components['schemas']['TransitionResult'];

export type CabinetComment = components['schemas']['CabinetComment'];
export type CabinetCommentPage = components['schemas']['CabinetCommentPage'];
export type CommentReplyRequest = components['schemas']['CommentReplyRequest'];

export type PaymentsView = components['schemas']['PaymentsView'];
/**
 * The open month, priced provisionally, ruled by the owner on 2026-08-26 (decision-log row 138
 * item 1). It is a PROPERTY OF `getPaymentsView`, not a second operation, and it creates no stored
 * balance: "computed at read time … stored nowhere" (authors-api.d.ts PaymentsView.currentMonth).
 */
export type CurrentMonth = NonNullable<PaymentsView['currentMonth']>;
export type PayoutEntry = NonNullable<PaymentsView['payouts']>[number];

export type Statement = components['schemas']['Statement'];
export type StatementLine = Statement['lines'][number];
export type StatementPage = components['schemas']['StatementPage'];
/**
 * A ROW IN THE STATEMENT LIST IS NOT A STATEMENT. `listStatements` returns `{ period,
 * settledAmountMinor, currency, status?, renderedAt? }` and nothing else — no text, no lines. The
 * document itself costs a second read (`getStatement`), which is why the list opens a panel rather
 * than expanding a row it already has.
 */
export type StatementSummary = NonNullable<StatementPage['items']>[number];
export type PayoutMethod = components['schemas']['PayoutMethod'];

/* ---------------------------------------------------------------------------------------------
 * The status vocabulary, as a value.
 *
 * `ArticleStatus` is a union of string literals in the generated types and a type cannot be
 * iterated, so the list below is the one place a value form exists. `satisfies` is what keeps it
 * honest: add a status upstream and this file stops compiling.
 *
 * `manual_review` IS NOT HERE AND MUST NEVER BE: it is a work-queue state, never an article status
 * (authors-cabinet spec, screen "Articles list").
 * ------------------------------------------------------------------------------------------- */
export const ARTICLE_STATUSES = [
  'draft',
  'in_review',
  'changes_requested',
  'scheduled',
  'published',
  'unpublished',
  'archived',
] as const satisfies readonly ArticleStatus[];

/* ---------------------------------------------------------------------------------------------
 * The two 202 bodies. `OperationResult` reads 200 and then 201 and gives `void` for anything else,
 * so an operation that answers 202 reaches its schema here, by name, rather than by hand.
 * ------------------------------------------------------------------------------------------- */

/** `requestAccountDeletion`'s 202 (`CabinetAccountApi` in ./cabinet-api.ts). */
export type SchemaAccountDeletionAck = components['schemas']['AccountDeletionAck'];
/**
 * `requestSupporterMessageTranslation`'s 202: `queued` or `produced`, and nothing else - the
 * platform stores finished translations only, so no other state is knowable. The text itself is
 * read back from the message on the next list read.
 */
export type TranslationState = components['schemas']['TranslationState'];

/* ---------------------------------------------------------------------------------------------
 * What readers send the author (register R-006) and who the readers were (register R-020).
 * ------------------------------------------------------------------------------------------- */

/** One message a reader sent. NO SENDER IDENTITY IS CARRIED - no id, no handle, no label. */
export type SupporterMessage = components['schemas']['SupporterMessage'];
export type SupporterMessagePage = components['schemas']['SupporterMessagePage'];
/** The one reply a message may ever have: written once, never edited, retracted by an event. */
export type SupporterMessageReply = components['schemas']['SupporterMessageReply'];

/** One sealed month on one dimension. NO TOTAL, by design: see `routes/articles/AudiencePanel`. */
export type AudienceBreakdown = components['schemas']['AudienceBreakdown'];
export type AudienceDimension = components['schemas']['AudienceDimension'];

/* ---------------------------------------------------------------------------------------------
 * Video (v1.0.29): a video is a kind of article, with a video in place of pictures.
 * ------------------------------------------------------------------------------------------- */

export type ArticleKind = components['schemas']['ArticleKind'];
/** Whether staff let this author publish videos. Absent on a server from before it: read as none. */
export type VideoAccess = NonNullable<AuthorSession['videoAccess']>;
export type ArticleVideo = components['schemas']['ArticleVideo'];
export type ArticleVideoRequest = components['schemas']['ArticleVideoRequest'];
/** One rule of the video guidelines the author confirms when sending a video. */
export type VideoRule = components['schemas']['VideoRule'];
/**
 * The optional body of submit and resubmit. Reached by name: both operations declare their body
 * optional, which `OperationBody` (above) does not read.
 */
export type SubmitRequest = components['schemas']['SubmitRequest'];

/**
 * Live streams (1.2.0): a video born as a stream - a YouTube link with a planned start and end - by
 * a switch of its own beside video's. Absent on a server from before it: read as none.
 */
export type StreamAccess = NonNullable<AuthorSession['streamAccess']>;
/** `createVideoDraft`'s optional body (`OperationBody` does not read an optional one). */
export type CreateVideoDraftRequest = components['schemas']['CreateVideoDraftRequest'];
/** A stream's planned start and end as `setStreamTimes` takes and answers them. */
export type StreamTimes = components['schemas']['StreamTimes'];
/** `WorkingCopy.stream`: both `null` until the author sets them. */
export type WorkingStream = components['schemas']['WorkingStream'];
